'use client'

import * as React from 'react'

import { Avatar, AvatarFallback, type EncuentroAvatarSize } from '@/components/ui/avatar'
import { isGeneratedInitialsImage } from '@/lib/avatar/read'
import { cn } from '@/lib/utils'

import { useImageFailed } from './use-image-failed'

/**
 * The institution's face (its logo) wherever the institution is the author
 * or the actor: a post (card and detail), the sidebar. A logo is a mark, not a
 * photo: whole (`object-contain`) on the plain surface with a hairline, never
 * cropped and never over a tint. The initials, on the tint every avatar uses,
 * show only when there is no logo or it fails to load; never both at once.
 * A generated initials image (ui-avatars, what seed and demo orgs carry)
 * counts as no logo. Decorative: the name is written next to it.
 */
export function OrgAvatar({
  name,
  logoUrl,
  size = 'md',
  shape = 'circle',
  fallback = 'initials',
  className,
  'data-slot': slot = 'org-avatar',
}: {
  name: string
  logoUrl: string | null | undefined
  size?: EncuentroAvatarSize
  /** `square` is the sidebar's mark, next to the institution's name. */
  shape?: 'circle' | 'square'
  /** `none`: without a logo, nothing (the sidebar, where the name stands alone). */
  fallback?: 'initials' | 'none'
  className?: string
  'data-slot'?: string
}) {
  const url = logoUrl && !isGeneratedInitialsImage(logoUrl) ? logoUrl : null
  const { attach, failed, onError } = useImageFailed(url)
  const showLogo = url !== null && !failed
  const square = shape === 'square' && 'rounded-en-md'

  if (showLogo) {
    return (
      <Avatar
        look="encuentro"
        size={size}
        data-slot={slot}
        data-logo="true"
        className={cn('border border-en-line bg-en-surface', square, className)}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- logos are in public storage and often SVG; next/image would only pass them through */}
        <img
          ref={attach}
          src={url}
          alt=""
          decoding="async"
          onError={onError}
          className={cn('block size-full object-contain', shape === 'circle' ? 'p-2' : 'p-px')}
        />
      </Avatar>
    )
  }
  if (fallback === 'none') return null
  return (
    <Avatar look="encuentro" size={size} data-slot={slot} className={cn(square, className)}>
      <AvatarFallback look="encuentro" name={name} className={cn(square)} />
    </Avatar>
  )
}
